'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import AppLayout from '@/components/AppLayout';
import { createClient } from '@/lib/supabase/client';
import { Globe, Mic, FileText, CheckCircle, Users, AlertTriangle, BookOpen, Languages, ChevronRight, Search, Plus, ArrowRight, Volume2 } from 'lucide-react';

interface Language {
  id: string;
  name: string;
  native_name: string;
  data_status: string;
  geographic_regions: string[];
  dialects: string[];
  text_record_count: number;
  audio_record_count: number;
  validated_record_count: number;
  contributor_count: number;
  audio_minutes: number;
  description: string;
}

interface PlatformStats {
  totalLanguages: number;
  totalTextRecords: number;
  totalAudioRecords: number;
  totalValidated: number;
  totalContributors: number;
  totalAudioMinutes: number;
}

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string; dot: string }> = {
  well_represented:         { label: 'Well Represented',          color: '#1A6B3A', bg: '#1A6B3A18', dot: '#1A6B3A' },
  moderately_represented:   { label: 'Moderately Represented',    color: '#007070', bg: '#00707018', dot: '#007070' },
  under_resourced:          { label: 'Under-Resourced',           color: '#CC7700', bg: '#CC770018', dot: '#CC7700' },
  critically_under_resourced:{ label: 'Critically Under-Resourced', color: '#CC5500', bg: '#CC550018', dot: '#CC5500' },
  data_needed:              { label: 'Data Needed',               color: '#CC0000', bg: '#CC000018', dot: '#CC0000' },
};

export default function MutauroDashboard() {
  const [languages, setLanguages] = useState<Language[]>([]);
  const [stats, setStats] = useState<PlatformStats>({
    totalLanguages: 0, totalTextRecords: 0, totalAudioRecords: 0,
    totalValidated: 0, totalContributors: 0, totalAudioMinutes: 0,
  });
  const [loading, setLoading] = useState(true);
  const [selectedLang, setSelectedLang] = useState<Language | null>(null);
  const supabase = createClient();

  useEffect(() => {
    async function load() {
      try {
        const { data: langs } = await supabase
          .from('mutauro_languages')
          .select('*')
          .order('name');

        if (langs) {
          setLanguages(langs);
          setStats({
            totalLanguages: langs.length,
            totalTextRecords: langs.reduce((s, l) => s + (l.text_record_count || 0), 0),
            totalAudioRecords: langs.reduce((s, l) => s + (l.audio_record_count || 0), 0),
            totalValidated: langs.reduce((s, l) => s + (l.validated_record_count || 0), 0),
            totalContributors: langs.reduce((s, l) => s + (l.contributor_count || 0), 0),
            totalAudioMinutes: langs.reduce((s, l) => s + (l.audio_minutes || 0), 0),
          });
        }
      } catch (e) {
        // silently handle
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const statCards = [
    { label: 'Languages', value: stats.totalLanguages, icon: Languages, color: '#CC0000' },
    { label: 'Text Records', value: stats.totalTextRecords.toLocaleString(), icon: FileText, color: '#007070' },
    { label: 'Audio Recordings', value: stats.totalAudioRecords.toLocaleString(), icon: Mic, color: '#7B6FA3' },
    { label: 'Validated Records', value: stats.totalValidated.toLocaleString(), icon: CheckCircle, color: '#1A6B3A' },
    { label: 'Contributors', value: stats.totalContributors.toLocaleString(), icon: Users, color: '#CC7700' },
    { label: 'Audio Minutes', value: stats.totalAudioMinutes.toLocaleString(), icon: Volume2, color: '#CC5500' },
  ];

  const criticalLangs = languages.filter(l =>
    l.data_status === 'critically_under_resourced' || l.data_status === 'data_needed'
  );

  return (
    <AppLayout>
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-8">

        {/* ── Header ── */}
        <div className="mb-8">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-mono uppercase tracking-widest px-2 py-0.5 rounded"
                  style={{ background: '#CC000018', color: '#CC0000', fontFamily: 'var(--font-mono)' }}>
                  Mutauro AI
                </span>
                <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>MVP v0.1</span>
              </div>
              <h1 className="text-3xl font-bold mb-1" style={{ color: 'var(--foreground)', fontFamily: 'var(--font-sans)' }}>
                Zimbabwe Language Commons
              </h1>
              <p className="text-base max-w-2xl" style={{ color: 'var(--muted-foreground)' }}>
                Preserving, documenting and building AI infrastructure for Zimbabwe's indigenous languages.
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Link href="/mutauro/contribute"
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                style={{ background: '#CC0000', color: '#fff' }}>
                <Plus size={15} /> Contribute Data
              </Link>
              <Link href="/mutauro/search"
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-colors"
                style={{ borderColor: 'var(--border)', color: 'var(--foreground)', background: 'var(--card)' }}>
                <Search size={15} /> Search Dataset
              </Link>
              <Link href="/mutauro/validate"
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-colors"
                style={{ borderColor: 'var(--border)', color: 'var(--foreground)', background: 'var(--card)' }}>
                <CheckCircle size={15} /> Validate
              </Link>
            </div>
          </div>
        </div>

        {/* ── Stats Grid ── */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
          {statCards.map((s) => (
            <div key={s.label} className="rounded-xl p-4 border"
              style={{ background: 'var(--card)', borderColor: 'var(--border)' }}>
              <div className="flex items-center gap-2 mb-2">
                <s.icon size={16} style={{ color: s.color }} />
                <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{s.label}</span>
              </div>
              <div className="text-2xl font-bold" style={{ color: 'var(--foreground)', fontFamily: 'var(--font-sans)' }}>
                {loading ? '—' : s.value}
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* ── Language Explorer ── */}
          <div className="lg:col-span-2">
            <div className="rounded-xl border overflow-hidden" style={{ background: 'var(--card)', borderColor: 'var(--border)' }}>
              <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: 'var(--border)' }}>
                <div className="flex items-center gap-2">
                  <Globe size={16} style={{ color: '#CC0000' }} />
                  <span className="font-semibold text-sm" style={{ color: 'var(--foreground)' }}>Language Explorer</span>
                </div>
                <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{languages.length} languages</span>
              </div>
              <div className="divide-y" style={{ divideColor: 'var(--border)' }}>
                {loading ? (
                  <div className="p-8 text-center text-sm" style={{ color: 'var(--muted-foreground)' }}>Loading languages…</div>
                ) : languages.map((lang) => {
                  const sc = STATUS_CONFIG[lang.data_status] || STATUS_CONFIG.data_needed;
                  const isSelected = selectedLang?.id === lang.id;
                  return (
                    <div key={lang.id}>
                      <button
                        onClick={() => setSelectedLang(isSelected ? null : lang)}
                        className="w-full text-left px-5 py-3.5 flex items-center justify-between hover:bg-opacity-50 transition-colors"
                        style={{ background: isSelected ? sc.bg : 'transparent' }}>
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: sc.dot }} />
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-sm" style={{ color: 'var(--foreground)' }}>{lang.name}</span>
                              {lang.native_name && (
                                <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{lang.native_name}</span>
                              )}
                            </div>
                            <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                              <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: sc.bg, color: sc.color }}>{sc.label}</span>
                              {lang.dialects?.length > 0 && (
                                <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{lang.dialects.length} dialects</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 flex-shrink-0 ml-3">
                          <div className="text-right hidden sm:block">
                            <div className="text-xs font-mono" style={{ color: 'var(--foreground)', fontFamily: 'var(--font-mono)' }}>
                              {(lang.text_record_count || 0).toLocaleString()}
                            </div>
                            <div className="text-xs" style={{ color: 'var(--muted-foreground)' }}>records</div>
                          </div>
                          <ChevronRight size={14} style={{ color: 'var(--muted-foreground)', transform: isSelected ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }} />
                        </div>
                      </button>
                      {isSelected && (
                        <div className="px-5 pb-4 pt-1" style={{ background: sc.bg }}>
                          <p className="text-sm mb-3" style={{ color: 'var(--muted-foreground)' }}>{lang.description}</p>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                            {[
                              { label: 'Text Records', val: lang.text_record_count || 0 },
                              { label: 'Audio', val: lang.audio_record_count || 0 },
                              { label: 'Validated', val: lang.validated_record_count || 0 },
                              { label: 'Contributors', val: lang.contributor_count || 0 },
                            ].map(item => (
                              <div key={item.label} className="rounded-lg p-2.5 border" style={{ background: 'var(--card)', borderColor: 'var(--border)' }}>
                                <div className="text-lg font-bold" style={{ color: 'var(--foreground)' }}>{item.val.toLocaleString()}</div>
                                <div className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{item.label}</div>
                              </div>
                            ))}
                          </div>
                          {lang.geographic_regions?.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mb-2">
                              {lang.geographic_regions.map(r => (
                                <span key={r} className="text-xs px-2 py-0.5 rounded-full border" style={{ borderColor: 'var(--border)', color: 'var(--muted-foreground)' }}>{r}</span>
                              ))}
                            </div>
                          )}
                          <Link href={`/mutauro/contribute?lang=${lang.id}`}
                            className="inline-flex items-center gap-1.5 text-xs font-medium mt-1"
                            style={{ color: '#CC0000' }}>
                            Contribute to {lang.name} <ArrowRight size={12} />
                          </Link>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* ── Right Column ── */}
          <div className="flex flex-col gap-5">

            {/* Data Gap Dashboard */}
            <div className="rounded-xl border overflow-hidden" style={{ background: 'var(--card)', borderColor: 'var(--border)' }}>
              <div className="px-5 py-4 border-b flex items-center gap-2" style={{ borderColor: 'var(--border)' }}>
                <AlertTriangle size={16} style={{ color: '#CC0000' }} />
                <span className="font-semibold text-sm" style={{ color: 'var(--foreground)' }}>Data Gap Dashboard</span>
              </div>
              <div className="p-4 space-y-3">
                {loading ? (
                  <div className="text-sm text-center py-4" style={{ color: 'var(--muted-foreground)' }}>Loading…</div>
                ) : criticalLangs.slice(0, 6).map((lang) => {
                  const sc = STATUS_CONFIG[lang.data_status];
                  const pct = lang.validated_record_count && lang.text_record_count
                    ? Math.round((lang.validated_record_count / Math.max(lang.text_record_count, 1)) * 100)
                    : 0;
                  return (
                    <div key={lang.id} className="rounded-lg p-3 border" style={{ borderColor: 'var(--border)', background: sc?.bg || '#CC000010' }}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>{lang.name}</span>
                        <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: sc?.bg, color: sc?.color }}>
                          {lang.data_status === 'data_needed' ? '🔴 Data Needed' : '🟠 Critical Gap'}
                        </span>
                      </div>
                      <div className="text-xs mb-1.5" style={{ color: 'var(--muted-foreground)' }}>
                        {lang.text_record_count || 0} text · {lang.audio_minutes || 0} min audio · {pct}% validated
                      </div>
                      <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--muted)' }}>
                        <div className="h-full rounded-full transition-all" style={{ width: `${Math.max(pct, 2)}%`, background: sc?.dot || '#CC0000' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="rounded-xl border overflow-hidden" style={{ background: 'var(--card)', borderColor: 'var(--border)' }}>
              <div className="px-5 py-4 border-b" style={{ borderColor: 'var(--border)' }}>
                <span className="font-semibold text-sm" style={{ color: 'var(--foreground)' }}>Quick Actions</span>
              </div>
              <div className="p-3 space-y-2">
                {[
                  { href: '/mutauro/contribute', icon: Plus, label: 'Submit Text / Audio', desc: 'Add language data' },
                  { href: '/mutauro/validate', icon: CheckCircle, label: 'Validate Submissions', desc: 'Review pending data' },
                  { href: '/mutauro/search', icon: Search, label: 'Search Dataset', desc: 'Find language records' },
                  { href: '/mutauro/dictionary', icon: BookOpen, label: 'Dictionary', desc: 'Browse word entries' },
                ].map(item => (
                  <Link key={item.href} href={item.href}
                    className="flex items-center gap-3 p-3 rounded-lg border transition-colors hover:border-red-200"
                    style={{ borderColor: 'var(--border)', background: 'var(--secondary)' }}>
                    <item.icon size={16} style={{ color: '#CC0000' }} />
                    <div className="min-w-0">
                      <div className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>{item.label}</div>
                      <div className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{item.desc}</div>
                    </div>
                    <ChevronRight size={14} className="ml-auto flex-shrink-0" style={{ color: 'var(--muted-foreground)' }} />
                  </Link>
                ))}
              </div>
            </div>

            {/* Platform Mission */}
            <div className="rounded-xl p-5 border" style={{ background: '#CC000008', borderColor: '#CC000030' }}>
              <div className="text-sm font-semibold mb-2" style={{ color: '#CC0000' }}>Platform Mission</div>
              <p className="text-xs leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                A digital infrastructure for preserving Zimbabwean languages and building AI systems that understand Zimbabwe — from speech recognition to civic AI tools.
              </p>
              <div className="mt-3 pt-3 border-t text-xs font-mono space-y-1" style={{ borderColor: '#CC000030', fontFamily: 'var(--font-mono)', color: 'var(--muted-foreground)' }}>
                <div>Target: 10+ languages</div>
                <div>Target: 100,000+ text records</div>
                <div>Target: 1,000+ hours audio</div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </AppLayout>
  );
}
